import React, { useState, useEffect } from "react";
import { FiArrowLeft, FiCheck } from "react-icons/fi";
import { db, auth } from "../../firebase";
import { collection, query, orderBy, onSnapshot, updateDoc, doc, serverTimestamp, getDocs, limit, setDoc, getDoc } from "firebase/firestore";
import { useLanguage } from "../../contexts/LanguageContext";
import { validateMessage } from "../../utils/profanityFilter";
import ProfanityModal from "../../components/usercomponents/ProfanityModal";
import "./staffcss/medicalappointments.css";
import Banner from "./Banner";

const MedicalEscalationForm = ({ caseData, onBack, readOnly = false, hideEscalationForm = false }) => {
  const { getTranslations } = useLanguage();
  const t = getTranslations(false, false, false, true);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("details");
  const [peerEducatorName, setPeerEducatorName] = useState("");
  
  // Escalation form state
  const [escalationForm, setEscalationForm] = useState({
    responseType: "",
    professionalMessage: "",
    recommendation: "",
    escalationStatus: "resolved"
  });

  // Validation errors state
  const [errors, setErrors] = useState({
    responseType: false,
    professionalMessage: false
  });

  // Review modal state
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showProfanityModal, setShowProfanityModal] = useState(false);

  // Banner state
  const [bannerMessage, setBannerMessage] = useState("");
  const [bannerType, setBannerType] = useState("success");

  const RESPONSE_TYPES = [
    "Health Information",
    "Guidance/Advice",
    "Clarification",
    "Recommendation",
    "Follow-up Instructions",
    "Referral"
  ];

  const getStatusLabel = (status, t) => {
    const mapping = {
      'resolved': t.resolved
    };
    return mapping[status] || status;
  };

  // Fetch peer educator's first name
  useEffect(() => {
    const fetchPeerEducatorName = async () => {
      try {
        let peerEducatorId = caseData?.peerEducatorId;

        // If peerEducatorId not in caseData, try to get it from chat document
        if (!peerEducatorId && caseData?.chatId) {
          const chatDoc = await getDoc(doc(db, 'chats', caseData.chatId));
          if (chatDoc.exists()) {
            const chatData = chatDoc.data();
            peerEducatorId = chatData.peerEducatorId || chatData.assignedTo;
          }
        }

        // Fetch user document once with the determined peerEducatorId
        if (peerEducatorId) {
          const userDoc = await getDoc(doc(db, 'users', peerEducatorId));
          if (userDoc.exists()) {
            const data = userDoc.data();
            const fullName = data.fullName || "";
            const firstName = fullName.split(' ')[0] || "Peer Educator";
            setPeerEducatorName(firstName);
          } else {
            setPeerEducatorName("Peer Educator");
          }
        } else {
          setPeerEducatorName("Peer Educator");
        }
      } catch (error) {
        console.error('Error fetching peer educator name:', error);
        setPeerEducatorName("Peer Educator");
      }
    };

    fetchPeerEducatorName();
  }, [caseData?.id, caseData?.chatId, caseData?.peerEducatorId]);
  useEffect(() => {
    if (!caseData?.chatId) {
      setLoading(false);
      return;
    }

    let mounted = true;
    setLoading(true);

    // Fetch messages from the messages subcollection using chatId - OPTIMIZED
    const messagesQuery = query(
      collection(db, "chats", caseData.chatId, "messages"),
      orderBy("createdAt", "asc"),
      limit(100) // Limit to prevent excessive reads for long conversations
    );

    const unsubscribe = onSnapshot(messagesQuery, (snapshot) => {
      if (!mounted) return;
      
      console.log("Messages snapshot size:", snapshot.size);
      console.log("Chat ID:", caseData.chatId);
      
      const messagesData = [];
      snapshot.forEach(doc => {
        const data = doc.data();
        messagesData.push({
          id: doc.id,
          createdAt: data.createdAt,
          text: data.text,
          senderRole: data.senderRole,
          senderId: data.senderId,
          ...data
        });
      });
      
      console.log("Messages data:", messagesData);
      setMessages(messagesData.sort((a, b) => {
        const orderA = a.order ?? 0;
        const orderB = b.order ?? 0;
        if (orderA !== orderB) return orderA - orderB;
        const timeA = a.createdAt?.toMillis?.() ?? 0;
        const timeB = b.createdAt?.toMillis?.() ?? 0;
        return timeA - timeB;
      }));
      setLoading(false);
    }, (error) => {
      console.error("Error fetching messages:", error);
      setLoading(false);
    });

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, [caseData?.chatId]);

  const handleResolve = async () => {
    // Validate required fields
    const newErrors = {
      responseType: !escalationForm.responseType,
      professionalMessage: !escalationForm.professionalMessage
    };

    setErrors(newErrors);

    if (newErrors.responseType || newErrors.professionalMessage) {
      return;
    }

    // Validate message for profanity
    const messageValidation = validateMessage(escalationForm.professionalMessage);
    if (!messageValidation.isValid) {
      setShowProfanityModal(true);
      return;
    }

    // Validate recommendation for profanity if present
    if (escalationForm.recommendation) {
      const recommendationValidation = validateMessage(escalationForm.recommendation);
      if (!recommendationValidation.isValid) {
        setShowProfanityModal(true);
        return;
      }
    }

    // Show review modal
    setShowReviewModal(true);
  };

  const handleConfirmResolve = async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        setBannerMessage("User not authenticated");
        setBannerType("error");
        return;
      }

      const selectedStatus = escalationForm.escalationStatus;

      // Update escalatedChats document with response
      await updateDoc(doc(db, "escalatedChats", caseData.id), {
        escalationStatus: selectedStatus,
        responseType: escalationForm.responseType,
        professionalMessage: escalationForm.professionalMessage,
        recommendation: escalationForm.recommendation,
        resolvedAt: serverTimestamp(),
        resolvedBy: user.uid
      });

      // Also update chat document and persist feedback message once
      if (caseData.chatId) {
        await updateDoc(doc(db, "chats", caseData.chatId), {
          escalationStatus: selectedStatus
        });

        if (selectedStatus === "resolved") {
          const responseType = escalationForm.responseType || "Response";
          const professionalMessage = escalationForm.professionalMessage || "";
          const recommendation = escalationForm.recommendation || "";

          let messageText = `${responseType}\n\nThank you for sharing your concern. ${professionalMessage}`;
          if (recommendation) {
            messageText += `\n\n${recommendation}`;
          }
          messageText += "\n\nYour concern has been reviewed by a Medical Professional.";

          const messagesRef = collection(db, "chats", caseData.chatId, "messages");
          const orderQuery = query(messagesRef, orderBy("order", "desc"), limit(1));
          const orderSnap = await getDocs(orderQuery);
          const nextOrder = orderSnap.empty
            ? 0
            : (orderSnap.docs[0].data().order ?? 0) + 1;

          await setDoc(
            doc(db, "chats", caseData.chatId, "messages", `medical-feedback-${caseData.id}`),
            {
              senderId: user.uid,
              senderRole: "medical professional",
              text: messageText,
              responseType,
              professionalMessage,
              recommendation,
              createdAt: serverTimestamp(),
              order: nextOrder,
              isMedicalFeedback: true,
              escalationId: caseData.id
            }
          );
        }
      }

      const statusLabel = getStatusLabel(selectedStatus, t);
      setBannerMessage(`Status updated to ${statusLabel}`);
      setBannerType("success");
      setShowReviewModal(false);
      setTimeout(() => onBack(), 1500);
    } catch (error) {
      console.error("Error resolving case:", error);
      setBannerMessage("Failed to resolve case");
      setBannerType("error");
    }
  };

  const getSenderLabel = (msg) => {
    if (msg.senderRole === "user") return "User";
    if (msg.senderRole === "medical professional") {
      const proFullName = caseData?.proName || "Medical Professional";
      const proFirstName = proFullName.split(' ')[0] || proFullName;
      return `Medical Professional - (${proFirstName})`;
    }
    if (msg.senderRole === "system" || msg.isEscalationNotice) return `Peer Educator - (${peerEducatorName || "Peer Educator"})`;
    if (msg.senderRole === "peer" || msg.senderRole === "peerEducator" || msg.senderRole === "peer educator") {
      return `Peer Educator - (${peerEducatorName || "Peer Educator"})`;
    }
    return `Peer Educator - (${peerEducatorName || "Peer Educator"})`;
  };

  const isUserMessage = (msg) => msg.senderRole === "user";

  const conversationPanelHeight = "calc(100vh - 200px)";

  return (
    <div
      className="ma-main"
      style={{
        padding: "24px",
        height: readOnly ? "calc(100vh - 48px)" : "auto",
        maxHeight: readOnly ? "calc(100vh - 48px)" : "none",
        overflow: readOnly ? "hidden" : "visible",
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box"
      }}
    >
      {bannerMessage && (
        <Banner
          message={bannerMessage}
          type={bannerType}
          onClose={() => setBannerMessage("")}
        />
      )}

      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '24px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button
            onClick={onBack}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 16px',
              borderRadius: '10px',
              border: '1px solid #e2e8f0',
              background: '#fff',
              color: '#64748b',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = '#f8fafc';
              e.target.style.borderColor = '#3b82f6';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = '#fff';
              e.target.style.borderColor = '#e2e8f0';
            }}
          >
            <FiArrowLeft />
          </button>
          <div>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '600',
              background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              margin: 0
            }}>
              {readOnly ? "Conversation History" : "Escalation Case"}
            </h2>
            <p style={{
              fontSize: '14px',
              color: '#64748b',
              margin: '4px 0 0 0'
            }}>
              Ticket: {caseData?.ticketNumber || "N/A"}
            </p>
          </div>
        </div>
        <div style={{
          padding: '8px 16px',
          borderRadius: '20px',
          background: caseData?.escalationStatus === 'resolved' 
            ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' 
            : 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
          color: '#fff',
          fontSize: '14px',
          fontWeight: '600'
        }}>
          {caseData?.escalationStatus === 'under-assessment' ? 'Under Assessment' : caseData?.escalationStatus === 'resolved' ? 'Resolved' : caseData?.escalationStatus || 'Unknown'}
        </div>
      </div>

      {/* Layout */}
      <div style={{
        display: "grid",
        gridTemplateColumns: readOnly ? "1fr" : "1fr 400px",
        gap: "24px",
        flex: readOnly ? 1 : "none",
        minHeight: 0,
        overflow: "hidden"
      }}>
        {/* Left Column - Conversation Panel */}
        <div style={{
          background: "#fff",
          borderRadius: "16px",
          border: "1px solid #e5e7eb",
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          height: conversationPanelHeight,
          maxHeight: conversationPanelHeight,
          minHeight: 0
        }}>
          <div style={{
            padding: '16px 20px',
            borderBottom: '1px solid #e5e7eb',
            background: '#f8fafc'
          }}>
            <h3 style={{
              fontSize: '16px',
              fontWeight: '600',
              background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              margin: 0
            }}>
              Conversation History
            </h3>
            <p style={{
              fontSize: '12px',
              color: '#64748b',
              margin: '4px 0 0 0'
            }}>
              Read-only view of the chat history
            </p>
          </div>
          
          <div style={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            padding: "20px"
          }}>
            {loading ? (
              <div style={{ textAlign: 'center', color: '#64748b', padding: '40px' }}>
                {t.loadingMessages}
              </div>
            ) : messages.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#64748b', padding: '40px' }}>
                {t.noMessages}
              </div>
            ) : (
              messages.map((msg) => {
                const isUser = isUserMessage(msg);
                const isSystem = msg.senderRole === "system" || msg.isEscalationNotice;
                const isMedical = msg.senderRole === "medical professional";
                
                return (
                  <div
                    key={msg.id}
                    style={{
                      marginBottom: '16px',
                      display: 'flex',
                      flexDirection: isUser ? 'row' : 'row-reverse'
                    }}
                  >
                    <div style={{
                      maxWidth: '70%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: isUser ? 'flex-start' : 'flex-end'
                    }}>
                      <div style={{
                        fontSize: '11px',
                        fontWeight: '600',
                        marginBottom: '4px',
                        color: '#64748b'
                      }}>
                        {getSenderLabel(msg)}
                      </div>
                      <div style={{
                        padding: '12px 16px',
                        borderRadius: '12px',
                        background: isUser
                          ? '#f1f5f9'
                          : isSystem
                            ? '#e0f2fe'
                            : isMedical
                              ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                              : 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
                        color: isUser || isSystem ? '#1e293b' : '#fff',
                        fontSize: '14px',
                        lineHeight: '1.5',
                        whiteSpace: 'pre-line',
                        textAlign: 'left'
                      }}>
                        {msg.text}
                      </div>
                      <div style={{
                        fontSize: '11px',
                        marginTop: '4px',
                        color: '#9ca3af'
                      }}>
                        {msg.createdAt?.toDate?.() ? msg.createdAt.toDate().toLocaleTimeString('en-US', {
                          hour: '2-digit',
                          minute: '2-digit'
                        }) : ''}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {!readOnly && (
        <div style={{
          background: '#fff',
          borderRadius: '16px',
          border: '1px solid #e5e7eb',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100vh - 200px)'
        }}>
          {/* Tab Headers */}
          <div style={{
            display: 'flex',
            borderBottom: '1px solid #e5e7eb',
            background: '#f8fafc'
          }}>
            <button
              onClick={() => setActiveTab("details")}
              style={{
                flex: 1,
                padding: '16px',
                border: 'none',
                background: activeTab === "details" ? 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)' : 'transparent',
                color: activeTab === "details" ? '#ffffff' : '#475569',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                borderBottom: activeTab === "details" ? '2px solid #3b82f6' : '2px solid transparent'
              }}
            >
              {t.escalationDetails}
            </button>
            {!hideEscalationForm && (
              <button
                onClick={() => setActiveTab("form")}
                style={{
                  flex: 1,
                  padding: '16px',
                  border: 'none',
                  background: activeTab === "form" ? 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)' : 'transparent',
                  color: activeTab === "form" ? '#ffffff' : '#475569',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  borderBottom: activeTab === "form" ? '2px solid #3b82f6' : '2px solid transparent'
                }}
              >
                {t.escalationFormTitle}
              </button>
            )}
          </div>

          {/* Tab Content */}
          <div style={{ padding: '20px', flex: 1, overflow: 'auto', textAlign: 'left' }}>
            {activeTab === "details" ? (
              <div>
                <h4 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#1e293b',
                  margin: '0 0 4px 0'
                }}>
                  {t.escalationDetails}
                </h4>
                <p style={{
                  fontSize: '12px',
                  color: '#64748b',
                  margin: '0 0 20px 0'
                }}>
                  Information about the escalated case
                </p>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#64748b',
                    marginBottom: '6px'
                  }}>
                    Topic
                  </label>
                  <div style={{
                    padding: '10px 14px',
                    borderRadius: '8px',
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    fontSize: '14px',
                    color: '#1e293b'
                  }}>
                    {caseData?.escalationTopic || "N/A"}
                  </div>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#64748b',
                    marginBottom: '6px'
                  }}>
                    Reason
                  </label>
                  <div style={{
                    padding: '10px 14px',
                    borderRadius: '8px',
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    fontSize: '14px',
                    color: '#1e293b'
                  }}>
                    {caseData?.escalationReason || "N/A"}
                  </div>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#64748b',
                    marginBottom: '6px'
                  }}>
                    Notes
                  </label>
                  <div style={{
                    padding: '10px 14px',
                    borderRadius: '8px',
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    fontSize: '14px',
                    color: '#1e293b',
                    minHeight: '80px'
                  }}>
                    {caseData?.escalationNotes || "N/A"}
                  </div>
                </div>

                {hideEscalationForm && (
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{
                      display: 'block',
                      fontSize: '12px',
                      fontWeight: '600',
                      color: '#64748b',
                      marginBottom: '6px'
                    }}>
                      Medical Professional Assessing
                    </label>
                    <div style={{
                      padding: '10px 14px',
                      borderRadius: '8px',
                      background: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      fontSize: '14px',
                      color: '#1e293b'
                    }}>
                      {caseData?.proName || "N/A"}
                    </div>
                  </div>
                )}
              </div>
            ) : !hideEscalationForm ? (
              <div>
                <h4 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#1e293b',
                  margin: '0 0 4px 0'
                }}>
                  {t.escalationFormTitle}
                </h4>
                <p style={{
                  fontSize: '12px',
                  color: '#64748b',
                  margin: '0 0 20px 0'
                }}>
                  Provide your response and resolution for this case
                </p>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#64748b',
                    marginBottom: '6px'
                  }}>
                    {t.responseType} <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <select
                    value={escalationForm.responseType}
                    onChange={(e) => {
                      setEscalationForm({...escalationForm, responseType: e.target.value});
                      setErrors({...errors, responseType: false});
                    }}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      borderRadius: '8px',
                      border: errors.responseType ? '1px solid #ef4444' : '1px solid #e2e8f0',
                      fontSize: '14px',
                      color: '#1e293b',
                      background: '#fff'
                    }}
                  >
                    <option value="">Select response type</option>
                    {RESPONSE_TYPES.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                  {errors.responseType && (
                    <div style={{ fontSize: '11px', color: '#ef4444', marginTop: '4px' }}>
                      Response type is required
                    </div>
                  )}
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#64748b',
                    marginBottom: '6px'
                  }}>
                    {t.responseMessage} <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <div style={{ position: 'relative' }}>
                    <textarea
                      value={escalationForm.professionalMessage}
                      onChange={(e) => {
                        if (e.target.value.length <= 500) {
                          setEscalationForm({...escalationForm, professionalMessage: e.target.value});
                          setErrors({...errors, professionalMessage: false});
                        }
                      }}
                      placeholder="Provide your response or guidance based on the user's concern."
                      style={{
                        width: '100%',
                        padding: '10px 14px',
                        paddingBottom: '30px',
                        borderRadius: '8px',
                        border: errors.professionalMessage ? '1px solid #ef4444' : '1px solid #e2e8f0',
                        fontSize: '14px',
                        color: '#1e293b',
                        background: '#fff',
                        minHeight: '120px',
                        resize: 'vertical',
                        fontFamily: 'inherit'
                      }}
                    />
                    <div style={{
                      position: 'absolute',
                      bottom: '8px',
                      right: '12px',
                      fontSize: '11px',
                      color: escalationForm.professionalMessage.length >= 500 ? '#ef4444' : '#9ca3af'
                    }}>
                      {escalationForm.professionalMessage.length}/500
                    </div>
                  </div>
                  {errors.professionalMessage && (
                    <div style={{ fontSize: '11px', color: '#ef4444', marginTop: '4px' }}>
                      {t.responseMessage} is required
                    </div>
                  )}
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#64748b',
                    marginBottom: '6px'
                  }}>
                    {t.recommendation}
                  </label>
                  <div style={{ position: 'relative' }}>
                    <textarea
                      value={escalationForm.recommendation}
                      onChange={(e) => {
                        if (e.target.value.length <= 500) {
                          setEscalationForm({...escalationForm, recommendation: e.target.value});
                        }
                      }}
                      placeholder="Add your recommendations for the user's next steps..."
                      style={{
                        width: '100%',
                        padding: '10px 14px',
                        paddingBottom: '30px',
                        borderRadius: '8px',
                        border: '1px solid #e2e8f0',
                        fontSize: '14px',
                        color: '#1e293b',
                        background: '#fff',
                        minHeight: '80px',
                        resize: 'vertical',
                        fontFamily: 'inherit'
                      }}
                    />
                    <div style={{
                      position: 'absolute',
                      bottom: '8px',
                      right: '12px',
                      fontSize: '11px',
                      color: escalationForm.recommendation.length >= 500 ? '#ef4444' : '#9ca3af'
                    }}>
                      {escalationForm.recommendation.length}/500
                    </div>
                  </div>
                </div>

                <button
                  onClick={handleResolve}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    padding: '12px 20px',
                    borderRadius: '10px',
                    border: 'none',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: '#fff',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    if (escalationForm.responseType && escalationForm.professionalMessage && escalationForm.resolutionStatus) {
                      e.target.style.transform = 'translateY(-1px)';
                      e.target.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  <FiCheck /> Resolve Case
                </button>
              </div>
            ) : null}
          </div>
        </div>
        )}
      </div>

      {/* Review Modal */}
      {showReviewModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '16px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
            textAlign: 'left'
          }}>
            <div style={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
              padding: '20px 24px',
              borderRadius: '16px 16px 0 0'
            }}>
              <h3 style={{
                fontSize: '20px',
                fontWeight: '600',
                color: '#fff',
                margin: 0
              }}>
                Review Escalation Form
              </h3>
            </div>
            <div style={{ padding: '24px' }}>

            <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
              <div style={{ flex: 1 }}>
                <label style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: '600',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  marginBottom: '6px'
                }}>
                  Topic
                </label>
                <div style={{
                  padding: '10px 14px',
                  borderRadius: '8px',
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  fontSize: '14px',
                  color: '#1e293b'
                }}>
                  {caseData?.escalationTopic || "N/A"}
                </div>
              </div>

              <div style={{ flex: 1 }}>
                <label style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: '600',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  marginBottom: '6px'
                }}>
                  Reason
                </label>
                <div style={{
                  padding: '10px 14px',
                  borderRadius: '8px',
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  fontSize: '14px',
                  color: '#1e293b'
                }}>
                  {caseData?.escalationReason || "N/A"}
                </div>
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                fontSize: '12px',
                fontWeight: '600',
                background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                marginBottom: '6px'
              }}>
                Notes
              </label>
              <div style={{
                padding: '10px 14px',
                borderRadius: '8px',
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                fontSize: '14px',
                color: '#1e293b'
              }}>
                {caseData?.escalationNotes || "N/A"}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
              <div style={{ flex: 1 }}>
                <label style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: '600',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  marginBottom: '6px'
                }}>
                  Response Type
                </label>
                <div style={{
                  padding: '10px 14px',
                  borderRadius: '8px',
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  fontSize: '14px',
                  color: '#1e293b'
                }}>
                  {escalationForm.responseType || "N/A"}
                </div>
              </div>

              <div style={{ flex: 1 }}>
                <label style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: '600',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  marginBottom: '6px'
                }}>
                  Escalation Status
                </label>
                <div style={{
                  padding: '10px 14px',
                  borderRadius: '8px',
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  fontSize: '14px',
                  color: '#1e293b'
                }}>
                  {escalationForm.escalationStatus === 'resolved' ? 'Resolved' : escalationForm.escalationStatus || "N/A"}
                </div>
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                fontSize: '12px',
                fontWeight: '600',
                background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                marginBottom: '6px'
              }}>
                Professional Message
              </label>
              <div style={{
                padding: '10px 14px',
                borderRadius: '8px',
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                fontSize: '14px',
                color: '#1e293b',
                whiteSpace: 'pre-wrap'
              }}>
                {escalationForm.professionalMessage || "N/A"}
              </div>
            </div>

            {escalationForm.recommendation && (
              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: '600',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  marginBottom: '6px'
                }}>
                  Recommendation
                </label>
                <div style={{
                  padding: '10px 14px',
                  borderRadius: '8px',
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  fontSize: '14px',
                  color: '#1e293b',
                  whiteSpace: 'pre-wrap'
                }}>
                  {escalationForm.recommendation}
                </div>
              </div>
            )}
            </div>

            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end',
              padding: '0 24px 24px 24px'
            }}>
              <button
                onClick={() => setShowReviewModal(false)}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  background: '#fff',
                  color: '#64748b',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmResolve}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Confirm Resolve
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Profanity Modal */}
      <ProfanityModal 
        isOpen={showProfanityModal} 
        onClose={() => setShowProfanityModal(false)} 
      />
    </div>
  );
};

export default MedicalEscalationForm;
